import styles from './Filter.module.css';

import React from 'react';

import { DateTime } from 'luxon';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';

import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

import { useTheme } from '@mui/material/styles';

import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import Search from '@mui/icons-material/Search';

export const Filter = ({ filters, onFilterChange, onClearFilters }) => {
    const [ isExpanded, setIsExpanded ] = React.useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const toggleExpanded = () => setIsExpanded(!isExpanded);

    const handleInputChange = (field, value) => {
        onFilterChange({ ...filters, [ field ]: value });
    };

    const daysOfWeek = [
        { value: 'sunday', label: 'Sun' },
        { value: 'monday', label: 'Mon' },
        { value: 'tuesday', label: 'Tue' },
        { value: 'wednesday', label: 'Wed' },
        { value: 'thursday', label: 'Thu' },
        { value: 'friday', label: 'Fri' },
        { value: 'saturday', label: 'Sat' }
    ];

    const hasActiveFilters = filters.search || filters.onlyShift || filters.days.length > 0 || filters.onlyAvailable || filters.startDate || filters.endDate;

    return (
        <div className={ styles.filterContainer }>
            <div className={ styles.filterHeader } onClick={ toggleExpanded }>
                <span>
                    { hasActiveFilters && <span className={ styles.activeIndicator }>●&nbsp;</span> }
                    <Icon component={ Search } sx={ { mr: 1 } } />
                    <Typography>Search</Typography>
                </span>
                <span>
                    <Icon>{ isExpanded ? <ExpandLess /> : <ExpandMore /> }</Icon>
                </span>
            </div>

            <div className={ `${ styles.filterContent } ${ isExpanded ? styles.expanded : styles.collapsed }` }>
                <div className={ styles.searchSection }>
                    <input
                        type="text"
                        placeholder="Search by name, event, or time (e.g., 'A', 'Race Day', '09:00')"
                        value={ filters.search }
                        onChange={ (e) => handleInputChange('search', e.target.value) }
                        className={ styles.searchInput }
                    />
                </div>

                <Divider sx={ { my: 2 } } />

                <div className={ styles.searchSection }>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={ filters.onlyShift || false }
                                onChange={ (e) => handleInputChange('onlyShift', e.target.checked) }
                            />
                        }
                        label="Search Shift Name Only"
                    />
                </div>

                <div className={ styles.availabilitySection }>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={ filters.onlyAvailable || false }
                                onChange={ (e) => handleInputChange('onlyAvailable', e.target.checked) }
                            />
                        }
                        label="Only Available Shifts"
                    />
                </div>

                <Divider sx={ { my: 2 } } />

                <Grid container spacing={ 1 } sx={ { width: '100%', display: 'flex', mb: 1 } }>
                    <Grid size={ 12 } display='flex' justifyContent='flex-start' alignItems='center'>
                        <Stack direction="column" spacing={ 1 } justifyContent='flex-start' alignItems="left">
                            <Typography variant="h6" color="textSecondary" sx={ { mb: 2 } }>
                                Filter by date range
                            </Typography>

                            <Typography variant="caption" color="textSecondary" sx={ { mb: 2 } }>
                                For a specific range, enter both dates. To filter from a date, enter only a start date. To filter up to a date, enter only an end date.
                            </Typography>
                        </Stack>
                    </Grid>

                    <Grid size={ 12 } columnSpacing={ 1 } display='flex' alignItems='center' gap={ isMobile ? 2 : 4 }>
                        <LocalizationProvider dateAdapter={ AdapterLuxon }>
                            <DatePicker
                                label="Start Date"
                                value={ filters.startDate ? DateTime.fromISO(filters.startDate) : null }
                                onChange={ (newDate) => onFilterChange({ ...filters, startDate: newDate ? newDate.toISODate() : null }) }
                            />
                            <DatePicker
                                label="End Date"
                                value={ filters.endDate ? DateTime.fromISO(filters.endDate) : null }
                                onChange={ (newDate) => onFilterChange({ ...filters, endDate: newDate ? newDate.toISODate() : null }) }
                            />
                        </LocalizationProvider>
                    </Grid>
                </Grid>

                <Divider sx={ { my: 2 } } />

                <Grid container spacing={ 1 } sx={ { width: '100%', display: 'flex', mb: 1 } }>
                    <Grid size={ 12 } display='flex' justifyContent='flex-start' alignItems='center'>
                        <Typography variant="h6" color="textSecondary" sx={ { mb: 2 } }>
                            Filter by day(s) of the week
                        </Typography>
                    </Grid>

                    <Grid size={ 12 } columnSpacing={ 1 } display='flex' alignItems='center'>
                        <Stack direction="row" className={ styles.dayChips } flexWrap="wrap" justifyContent="flex-start" alignItems="center">
                            { daysOfWeek.map(day => (
                                <Button
                                    key={ day.value }
                                    variant={ filters.days.includes(day.value) ? 'contained' : 'outlined' }
                                    onClick={ () => {
                                        const newDays = filters.days.includes(day.value)
                                            ? filters.days.filter(d => d !== day.value)
                                            : [ ...filters.days, day.value ];
                                        handleInputChange('days', newDays);
                                    } }
                                    size={ isMobile ? 'small' : 'medium' }
                                >
                                    { isMobile ? day.label.charAt(0) : day.label }
                                </Button>
                            )) }
                        </Stack>
                    </Grid>
                </Grid>

                <Divider sx={ { my: 2 } } />

                <Grid container spacing={ 1 } sx={ { width: '100%', display: 'flex', mb: 1 } }>
                    <Grid size={ { xs: 12, sm: 8 } } display='flex' justifyContent='flex-start' alignItems='center'>
                        <Stack direction="column" spacing={ 1 } justifyContent='flex-start' alignItems="left">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={ filters.excludeSearchParams || false }
                                        onChange={ (e) => handleInputChange('excludeSearchParams', e.target.checked) }
                                        disabled={ !hasActiveFilters }
                                    />
                                }
                                label="Invert search parameters"
                            />

                            <Typography variant="caption" color="textSecondary">
                                &quot;Dec&quot; will exclude any shifts in December
                            </Typography>

                            <Typography variant="caption" color="textSecondary">
                                Selecting &quot;Mon&quot; will hide all Monday shifts
                            </Typography>
                        </Stack>
                    </Grid>
                </Grid>

                <Divider sx={ { my: 2 } } />

                <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={ { mb: 1, mt: 2, width: '100%' } } spacing={ 2 }>
                    <Button
                        fullWidth
                        variant={ hasActiveFilters ? 'contained' : 'outlined' }
                        className={ styles.clearButton }
                        onClick={ onClearFilters }
                        disabled={ !hasActiveFilters }
                        color='secondary'
                    >
                        Clear Filters
                    </Button>
                </Stack>
            </div>
        </div>
    );
};